// ============================================================================
// INVITES VIEW — Tenant Admin Invite Management UI
//
// Allows tenant_admin to create and manage email invites for their clinic.
//
// Flow:
//   1. On mount: fetch pending invites for the admin's tenant
//   2. "Create Invite" button → opens modal (email, role, specialties if specialist)
//   3. "Create Invite" in modal calls createInvite() → adds to list + toast
//   4. Filter tabs: Pending | All
//   5. Revoke button calls revokeInvite() → updates status in list
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, Loader2, AlertCircle, CheckCircle, X, Clock,
  UserCheck, Ban, ChevronDown,
} from 'lucide-react';
import {
  createInvite,
  listInvites,
  revokeInvite,
  getPendingInvitesForEmail,
  getTenantUsers,
  getSpecialties,
  ROLES,
  ROLE_DISPLAY,
} from '../core/index.js';
import { checkEmailExistsCF } from '../core/functions.js';
import { useAuth } from '../auth/AuthContext.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Roles available for inviting (exclude platform_admin + pending)
const INVITABLE_ROLES = Object.values(ROLES).filter(
  (r) => r !== ROLES.PLATFORM_ADMIN && r !== ROLES.PENDING,
);

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? 0) * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusBadge(status) {
  switch (status) {
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
    case 'claimed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400">
          <UserCheck className="w-3 h-3" />
          Claimed
        </span>
      );
    case 'revoked':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400">
          <Ban className="w-3 h-3" />
          Revoked
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-500/20 text-slate-500">
          <Clock className="w-3 h-3" />
          Expired
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-400">
          {status}
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

function Toast({ message, type, onClose }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border transition-all
      ${type === 'success'
        ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
        : 'bg-red-500/20 border-red-500/30 text-red-300'}
    `}>
      {type === 'success'
        ? <CheckCircle className="w-4 h-4" />
        : <AlertCircle className="w-4 h-4" />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Invite Modal
// ---------------------------------------------------------------------------

function CreateInviteModal({ tenantId, adminUid, specialties, onClose, onCreated, activeFocusTarget = null }) {
  const [email, setEmail]                     = useState('');
  const [role, setRole]                       = useState(ROLES.GP_EMPLOYEE);
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState([]);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');

  const isSpecialist = role === ROLES.SPECIALIST || role === ROLES.SPECIALIST_ADMIN;

  const toggleSpecialty = (id) => {
    setSelectedSpecialtyIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleCreate = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (isSpecialist && selectedSpecialtyIds.length === 0) {
      setError('Select at least one specialty for Specialist role.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const emailLower = trimmed.toLowerCase();

      const [pendingInvites, tenantUsers, emailCheck] = await Promise.all([
        getPendingInvitesForEmail(emailLower).catch(() => []),
        getTenantUsers(tenantId).catch(() => []),
        checkEmailExistsCF(emailLower).catch(() => ({ exists: false })),
      ]);

      if (emailCheck.exists) {
        setError('An account already exists for this email. This user must sign in directly instead.');
        setSaving(false);
        return;
      }

      if (pendingInvites.length > 0) {
        setError('A pending invite already exists for this email address.');
        setSaving(false);
        return;
      }

      const existingMember = tenantUsers.find(
        (u) => (u.emailLower || u.email?.toLowerCase()) === emailLower,
      );
      if (existingMember) {
        setError('This email is already registered as a member of this organization.');
        setSaving(false);
        return;
      }

      const inviteId = await createInvite(
        tenantId,
        adminUid,
        trimmed,
        role,
        isSpecialist ? selectedSpecialtyIds : [],
      );
      onCreated(inviteId, trimmed, role, selectedSpecialtyIds);
      onClose();
    } catch (err) {
      setError(safeUserError(err) ?? 'Failed to create invite. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <h2 className="text-lg font-semibold text-white">Create Invite</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Recipient Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="colleague@clinic.com"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-all"
              disabled={saving}
            />
          </div>

          {/* Role + specialty access — guided-learning focus target */}
          <div
            data-learning-target="ta_invites_modal_role_specialty"
            className={`space-y-5${focusHighlight('ta_invites_modal_role_specialty', activeFocusTarget)}`}
          >
            {/* Role selector */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Assign Role
              </label>
              <div className="relative">
                <select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value);
                    setSelectedSpecialtyIds([]);
                    setError('');
                  }}
                  className="w-full appearance-none px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700/50 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-all pr-9"
                  disabled={saving}
                >
                  {INVITABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_DISPLAY[r]?.name ?? r}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Specialty selector — only for Specialist role */}
            {isSpecialist && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Assign Specialties <span className="text-red-400">*</span>
                </label>
                {specialties.length === 0 ? (
                  <p className="text-sm text-slate-500">No specialties found for this tenant.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {specialties.map((sp) => (
                      <label
                        key={sp.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSpecialtyIds.includes(sp.id)}
                          onChange={() => toggleSpecialty(sp.id)}
                          className="w-4 h-4 accent-violet-500"
                        />
                        <span className="text-sm text-slate-300">{sp.name ?? sp.id}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Expiry note */}
          <p className="text-xs text-slate-500">
            Invites expire after 14 days. The recipient must sign in with the invited email address.
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !email.trim() || (isSpecialist && selectedSpecialtyIds.length === 0)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Creating…' : 'Create Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

const focusHighlight = (target, activeFocusTarget) =>
  (target && activeFocusTarget === target ? ' ring-2 ring-cyan-400 animate-pulse rounded-xl' : '');

export default function InvitesView({ activeFocusTarget = null }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const adminUid = user?.uid;

  const [invitesList, setInvitesList]   = useState([]);
  const [specialties, setSpecialties]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState('');

  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending' | null (all)
  const [showModal, setShowModal]       = useState(false);
  const [revoking, setRevoking]         = useState(null);  // inviteId being revoked

  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Load invites + specialties
  const loadInvites = useCallback(async (filter) => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError('');
    try {
      const [invs, specsMap] = await Promise.all([
        listInvites(tenantId, filter),
        getSpecialties(tenantId).catch(() => ({})),
      ]);
      setInvitesList(invs);
      const specsArr = Object.entries(specsMap).map(([id, data]) => ({ id, ...data }));
      setSpecialties(specsArr);
    } catch (err) {
      setLoadError(safeUserError(err) ?? 'Failed to load invites.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadInvites(statusFilter);
  }, [loadInvites, statusFilter]);

  // After creating an invite, optimistically prepend it
  const handleCreated = useCallback((inviteId, recipientEmailRaw, role, specialtyIds) => {
    const now = new Date();
    const expiresAt = { seconds: Math.floor((Date.now() + 14 * 86400000) / 1000) };
    const newInvite = {
      id: inviteId,
      inviteId,
      tenantId,
      recipientEmailLower: recipientEmailRaw.toLowerCase(),
      recipientEmailRaw,
      role,
      specialtyIds,
      status: 'pending',
      createdAt: { toDate: () => now },
      expiresAt,
    };
    // Only show if current filter includes pending
    if (statusFilter === 'pending' || statusFilter === null) {
      setInvitesList((prev) => [newInvite, ...prev]);
    }
    showToast('Invite created — email sent to recipient.');
  }, [tenantId, statusFilter, showToast]);

  const handleRevoke = useCallback(async (inv) => {
    setRevoking(inv.id);
    try {
      await revokeInvite(inv.id, adminUid);
      if (statusFilter === 'pending') {
        // Remove from pending list
        setInvitesList((prev) => prev.filter((i) => i.id !== inv.id));
      } else {
        // Update status in-place
        setInvitesList((prev) =>
          prev.map((i) => (i.id === inv.id ? { ...i, status: 'revoked' } : i)),
        );
      }
      showToast('Invite revoked.');
    } catch (err) {
      showToast(err?.message ?? 'Failed to revoke invite.', 'error');
    } finally {
      setRevoking(null);
    }
  }, [adminUid, statusFilter, showToast]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div data-learning-target="ta_invites_header" className={focusHighlight('ta_invites_header', activeFocusTarget).trim()}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-violet-400" />
            </div>
            <h1 className="text-2xl font-semibold text-white">Invites</h1>
          </div>
          <p className="text-slate-400 text-sm ml-13">
            Send email invites to provision staff directly into your clinic.
          </p>
        </div>
        <button
          data-learning-target="ta_invites_create"
          onClick={() => setShowModal(true)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-all flex-shrink-0${focusHighlight('ta_invites_create', activeFocusTarget)}`}
        >
          <Plus className="w-4 h-4" />
          Create Invite
        </button>
      </div>

      {/* Filter tabs */}
      <div data-learning-target="ta_invites_tabs" className={`flex gap-2 mb-6${focusHighlight('ta_invites_tabs', activeFocusTarget)}`}>
        {[
          { label: 'Pending', value: 'pending' },
          { label: 'All', value: null },
        ].map(({ label, value }) => (
          <button
            key={label}
            onClick={() => setStatusFilter(value)}
            className={`px-4 py-1.5 rounded-xl text-sm font-medium transition-all ${
              statusFilter === value
                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      )}

      {/* Load error */}
      {!loading && loadError && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-400">{loadError}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !loadError && invitesList.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {statusFilter === 'pending' ? 'No pending invites' : 'No invites yet'}
          </h3>
          <p className="text-slate-400 text-sm mb-6">
            Create an invite to provision staff without requiring manual Clinic ID entry.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-all"
          >
            <Plus className="w-4 h-4" />
            Create First Invite
          </button>
        </div>
      )}

      {/* Invites list */}
      {!loading && !loadError && invitesList.length > 0 && (
        <div className="space-y-3">
          {invitesList.map((inv, invIdx) => (
            <div
              key={inv.id}
              {...(invIdx === 0 ? { 'data-learning-target': 'ta_invites_actions' } : {})}
              className={`flex items-center justify-between gap-4 p-5 bg-slate-900/60 border border-slate-700/50 rounded-2xl${invIdx === 0 ? focusHighlight('ta_invites_actions', activeFocusTarget) : ''}`}
            >
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <p className="text-sm font-medium text-white truncate">
                    {inv.recipientEmailRaw || inv.recipientEmailLower}
                  </p>
                  {statusBadge(inv.status)}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs text-slate-400">
                    {ROLE_DISPLAY[inv.role]?.name ?? inv.role}
                  </span>
                  {inv.specialtyIds?.length > 0 && (
                    <span className="text-xs text-slate-500">
                      {inv.specialtyIds.length} specialty{inv.specialtyIds.length !== 1 ? 'ies' : ''}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">
                    Created {formatDate(inv.createdAt)}
                  </span>
                  {inv.status === 'pending' && inv.expiresAt && (
                    <span className="text-xs text-slate-500">
                      Expires {formatDate(inv.expiresAt)}
                    </span>
                  )}
                </div>
              </div>

              {/* Revoke button — only for pending invites */}
              {inv.status === 'pending' && (
                <button
                  onClick={() => handleRevoke(inv)}
                  disabled={revoking === inv.id}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-red-500/20 hover:text-red-400 text-slate-400 text-sm font-medium disabled:opacity-50 transition-all flex-shrink-0"
                >
                  {revoking === inv.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Ban className="w-4 h-4" />}
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Invite Modal */}
      {showModal && (
        <CreateInviteModal
          tenantId={tenantId}
          adminUid={adminUid}
          specialties={specialties}
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
          activeFocusTarget={activeFocusTarget}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
